import { Injectable } from '@angular/core';

interface CacheEntry<T> {
  key: string;
  data: T;
  timestamp: number;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class CachePersistentService {
  private dbName = 'ComunesCache';
  private storeName = 'cache-entries';
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void>;

  constructor() {
    this.initPromise = this.initDB();
  }

  /**
   * Inicializa la base de datos IndexedDB
   */
  private initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        console.warn('IndexedDB no disponible en el navegador');
        reject(new Error('IndexedDB not available'));
        return;
      }

      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => {
        console.error('Error al abrir IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('expiresAt', 'expiresAt', { unique: false });
        }
      };
    });
  }

  /**
   * Verifica si el caché está disponible
   */
  private async ensureDB(): Promise<void> {
    if (!this.db) {
      await this.initPromise;
    }
  }

  /**
   * Obtiene un valor del caché persistente
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      await this.ensureDB();
      if (!this.db) return null;

      return new Promise((resolve) => {
        const transaction = this.db!.transaction(this.storeName, 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.get(key);

        request.onsuccess = () => {
          const entry = request.result as CacheEntry<T> | undefined;
          if (entry && entry.expiresAt > Date.now()) {
            resolve(entry.data);
          } else if (entry) {
            // Caché expirado, eliminarlo
            this.delete(key).catch(() => {});
            resolve(null);
          } else {
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error('Error al leer caché:', request.error);
          resolve(null);
        };
      });
    } catch (error) {
      console.warn('Error accediendo al caché persistente:', error);
      return null;
    }
  }

  /**
   * Almacena un valor en el caché persistente
   */
  async set<T>(key: string, data: T, ttlMinutes: number = 1440): Promise<void> {
    try {
      await this.ensureDB();
      if (!this.db) return;

      const entry: CacheEntry<T> = {
        key,
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + ttlMinutes * 60 * 1000,
      };

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.put(entry);

        request.onsuccess = () => resolve();
        request.onerror = () => {
          console.error('Error al escribir caché:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.warn('Error almacenando en caché persistente:', error);
    }
  }

  /**
   * Elimina un valor del caché
   */
  async delete(key: string): Promise<void> {
    try {
      await this.ensureDB();
      if (!this.db) return;

      return new Promise((resolve) => {
        const transaction = this.db!.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.delete(key);

        request.onsuccess = () => resolve();
        request.onerror = () => {
          console.error('Error al eliminar caché:', request.error);
          resolve();
        };
      });
    } catch (error) {
      console.warn('Error eliminando caché persistente:', error);
    }
  }

  /**
   * Limpia todo el caché
   */
  async clear(): Promise<void> {
    try {
      await this.ensureDB();
      if (!this.db) return;

      return new Promise((resolve) => {
        const transaction = this.db!.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = () => {
          console.error('Error al limpiar caché:', request.error);
          resolve();
        };
      });
    } catch (error) {
      console.warn('Error limpiando caché persistente:', error);
    }
  }

  /**
   * Obtiene el tamaño aproximado del caché
   */
  async getSize(): Promise<number> {
    try {
      await this.ensureDB();
      if (!this.db) return 0;

      return new Promise((resolve) => {
        const transaction = this.db!.transaction(this.storeName, 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.getAll();

        request.onsuccess = () => {
          const entries = request.result as CacheEntry<any>[];
          const size = entries.reduce((acc, entry) => {
            return acc + JSON.stringify(entry).length;
          }, 0);
          resolve(size);
        };

        request.onerror = () => {
          console.error('Error al obtener tamaño del caché:', request.error);
          resolve(0);
        };
      });
    } catch (error) {
      console.warn('Error obteniendo tamaño del caché:', error);
      return 0;
    }
  }

  /**
   * Limpia cachés expirados
   */
  async cleanExpired(): Promise<void> {
    try {
      await this.ensureDB();
      if (!this.db) return;

      return new Promise((resolve) => {
        const transaction = this.db!.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const index = store.index('expiresAt');
        const range = IDBKeyRange.upperBound(Date.now());
        const request = index.getAll(range);

        request.onsuccess = () => {
          const entries = request.result as CacheEntry<any>[];
          entries.forEach((entry) => {
            store.delete(entry.key);
          });
        };

        transaction.oncomplete = () => resolve();
        transaction.onerror = () => {
          console.error('Error limpiando cachés expirados:', transaction.error);
          resolve();
        };
      });
    } catch (error) {
      console.warn('Error limpiando cachés expirados:', error);
    }
  }
}
